var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { FlagsModule } from '@seddi/ui-feature-flags';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { downloadImage } from '@/actions/captureImage';
import { setScene } from '@/actions/setScene';
import { saveCustomizerRecipe } from '@/api/styleAggregator';
import { buildRecipeBody } from '@/domain/recipe/buildRecipeBody';
import { setViewerRef } from '@/store/appRefsSlice';
import { getSceneDesc, getStyleSceneDesc } from '@/store/appSlice';
import { computePreviewLookAt } from '@/viewer/mockupLookAt';
import { canStartSaveExit } from './saveExit';
import * as viewerScene from './viewerScene';
const featureFlagsDef = {
    depthPeelingLayers: 0,
    disableTransparencyAngleBasedCompensation: false,
    disableTransparencyFlatCompensation: false,
    parallax: false,
    premultipliedAlphaLogos: true,
    wireframeFabrics: false,
};
const visibleDef = {
    avatar: false,
    background: false,
    garmentHem: true,
    garmentPiece: true,
    garmentSeam: true,
    ground: false,
    logos: true,
};
/**
 * Create a mapping from piece/mesh name to entity ID for logo placement.
 * Supports both garmentPiece entities (by name) and model entities (by meshDecals).
 */
const createPieceNameToEntityDict = (scene) => {
    var _a, _b, _c;
    const dict = {};
    for (const e of scene.children) {
        if ((_a = e.components) === null || _a === void 0 ? void 0 : _a.garmentPiece) {
            dict[e.name] = e._id;
        }
        if (((_b = e.components) === null || _b === void 0 ? void 0 : _b.model) && scene.models) {
            const modelId = e.components.model.model;
            const model = scene.models.find((m) => m.id === modelId);
            const meshDecals = (_c = model === null || model === void 0 ? void 0 : model.meta) === null || _c === void 0 ? void 0 : _c.meshDecals;
            if (meshDecals) {
                for (const name of meshDecals) {
                    dict[name] = e._id;
                }
            }
        }
    }
    return dict;
};
export const useAppViewer = (showGraphicsPanel = false, useBrandLogos = true) => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const mockupLogos = useSelector((state) => state.app.mockupLogos);
    const sceneIsLoading = useSelector((state) => state.app.sceneIsLoading);
    const sceneVersion = useSelector((state) => state.app.sceneVersion);
    const style = useSelector((state) => state.app.style);
    const styleNotFoundMessage = useSelector((state) => state.app.styleNotFoundMessage);
    const stylePreviewUrl = useSelector((state) => state.app.stylePreviewUrl);
    const styleRegions = useSelector((state) => state.app.styleRegions);
    const mockupHideTearAwayLabel = useSelector((state) => state.app.mockupHideTearAwayLabel);
    const selectedGraphicId = useSelector((state) => state.app.selectedGraphicId);
    const hoverGraphicId = useSelector((state) => state.app.hoverGraphicId);
    const recipeSource = useSelector((state) => state.app.recipeSource);
    const recipeLogoBaseline = useSelector((state) => state.app.recipeLogoBaseline);
    const pointsOfMeasure = useSelector((state) => state.app.pointsOfMeasure);
    const viewerRef = useRef(null);
    const lastFramedSceneVersionRef = useRef(null);
    const adapterRef = useRef(viewerScene.create());
    const saveInFlightRef = useRef(false);
    const [localSceneVersion, setLocalSceneVersion] = useState(0);
    const [waitingForFirstFrame, setWaitingForFirstFrame] = useState(false);
    const [saveExitStatus, setSaveExitStatus] = useState('idle');
    const [savedRecipeUrl, setSavedRecipeUrl] = useState('');
    const [savedRecipeExpiresAt, setSavedRecipeExpiresAt] = useState('');
    const [saveExitError, setSaveExitError] = useState('');
    const [recipeUrlCopyStatus, setRecipeUrlCopyStatus] = useState('idle');
    const logoRegions = useMemo(() => {
        const baseScene = getStyleSceneDesc();
        if (!baseScene || !styleRegions.length) {
            return [];
        }
        const pieceNameToEntityId = createPieceNameToEntityDict(baseScene);
        return styleRegions.map(r => {
            var _a;
            return (Object.assign(Object.assign({}, r), { entityId: (_a = pieceNameToEntityId[r.pieceName]) !== null && _a !== void 0 ? _a : '' }));
        });
    }, [styleRegions]);
    useEffect(() => {
        // communicate viewerRef through state
        dispatch(setViewerRef(viewerRef));
        // clear viewerRef state
        return () => {
            dispatch(setViewerRef(null));
        };
    }, [dispatch]);
    // Set style scene and regions when base scene becomes available.
    // Assumes baseScene is set synchronously with styleRegions.
    useEffect(() => {
        const baseScene = getStyleSceneDesc();
        if (!baseScene)
            return;
        const adapter = adapterRef.current;
        viewerScene.setStyleScene(adapter, baseScene, mockupHideTearAwayLabel, useBrandLogos);
        if (!styleRegions.length) {
            setLocalSceneVersion(v => v + 1);
            return;
        }
        const pieceNameToEntityId = createPieceNameToEntityDict(baseScene);
        const logoRegions = styleRegions.map(r => {
            var _a;
            return (Object.assign(Object.assign({}, r), { entityId: (_a = pieceNameToEntityId[r.pieceName]) !== null && _a !== void 0 ? _a : '' }));
        });
        viewerScene.setStyleRegions(adapter, logoRegions);
        setLocalSceneVersion(v => v + 1);
    }, [styleRegions, mockupHideTearAwayLabel, useBrandLogos]);
    // Reconcile logos when mockupLogos change.
    useEffect(() => {
        const baseScene = getStyleSceneDesc();
        if (!baseScene)
            return;
        const adapter = adapterRef.current;
        viewerScene.reconcileLogos(adapter, mockupLogos);
        setLocalSceneVersion(v => v + 1);
    }, [mockupLogos]);
    // Reconcile selection (style region border) when selected graphic changes.
    useEffect(() => {
        if (!showGraphicsPanel)
            return;
        const baseScene = getStyleSceneDesc();
        if (!baseScene)
            return;
        const selectedLogo = selectedGraphicId
            ? mockupLogos.find(l => l.id === selectedGraphicId)
            : undefined;
        const adapter = adapterRef.current;
        viewerScene.reconcileSelection(adapter, selectedLogo).then(() => {
            setLocalSceneVersion(v => v + 1);
        });
    }, [showGraphicsPanel, selectedGraphicId, mockupLogos]);
    // Reconcile hover (logo border) when hover or selection changes.
    useEffect(() => {
        if (!showGraphicsPanel)
            return;
        const baseScene = getStyleSceneDesc();
        if (!baseScene)
            return;
        const hoverLogo = hoverGraphicId ? mockupLogos.find(l => l.id === hoverGraphicId) : undefined;
        const selectedLogo = selectedGraphicId
            ? mockupLogos.find(l => l.id === selectedGraphicId)
            : undefined;
        const logoToShowBorder = hoverLogo !== null && hoverLogo !== void 0 ? hoverLogo : selectedLogo;
        const useSelectedColor = !hoverLogo && !!selectedLogo;
        const adapter = adapterRef.current;
        viewerScene.reconcileHover(adapter, logoToShowBorder, useSelectedColor).then(() => {
            setLocalSceneVersion(v => v + 1);
        });
    }, [showGraphicsPanel, hoverGraphicId, selectedGraphicId, mockupLogos]);
    // Clear hover and selection overlays when graphics panel is hidden.
    useEffect(() => {
        if (showGraphicsPanel)
            return;
        const baseScene = getStyleSceneDesc();
        if (!baseScene)
            return;
        const adapter = adapterRef.current;
        viewerScene.reconcileHover(adapter, undefined, false).then(() => {
            setLocalSceneVersion(v => v + 1);
        });
        viewerScene.reconcileSelection(adapter, undefined).then(() => {
            setLocalSceneVersion(v => v + 1);
        });
    }, [showGraphicsPanel]);
    // Update composed scene and propagate to viewer when sceneVersion changes.
    useEffect(() => {
        const adapter = adapterRef.current;
        viewerScene.updateScene(adapter);
        if (adapter.scene) {
            setScene(dispatch, adapter.scene);
        }
    }, [localSceneVersion, dispatch]);
    const config = useMemo(() => {
        const useStitchesDecals = FlagsModule.isEnabled('use_stitches_decals');
        return { frustumCulling: false, useStitchesDecals };
    }, []);
    const scene = useMemo(() => {
        return getSceneDesc();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sceneVersion is intended to memoize the new scene
    }, [sceneVersion]);
    const featureFlags = featureFlagsDef;
    const visible = visibleDef;
    const handleDownloadImage = useCallback(() => {
        if (viewerRef.current) {
            downloadImage(viewerRef.current, 'image/webp');
        }
    }, []);
    // Clear waitingForFirstFrame when viewer has rendered a frame (avoids empty-page flash).
    const handleUpdateWithFirstFrame = useCallback(() => {
        setWaitingForFirstFrame(prev => (prev ? false : prev));
    }, []);
    // Callback that fires after each render completes.
    const handleUpdate = useCallback(() => {
        handleUpdateWithFirstFrame();
    }, [handleUpdateWithFirstFrame]);
    const frameScene = useCallback((options) => {
        var _a;
        const { force = false } = options !== null && options !== void 0 ? options : {};
        if (!scene || sceneIsLoading || !style) {
            return;
        }
        if (!force && lastFramedSceneVersionRef.current === sceneVersion) {
            return;
        }
        const viewer = viewerRef.current;
        if (!viewer) {
            return;
        }
        const canvas = (_a = viewer.canvasRef) === null || _a === void 0 ? void 0 : _a.current;
        if (!canvas || canvas.width === 0 || canvas.height === 0) {
            return;
        }
        const baseScene = getStyleSceneDesc();
        const aspectRatio = canvas.width / canvas.height;
        const camera = computePreviewLookAt(style, styleRegions, baseScene, mockupLogos, viewer.camera.fov, aspectRatio);
        const tool = viewer.tool;
        tool.lookAt(camera.origin, camera.target);
        // set zoommax distance to initial camera distance
        if (typeof tool.setMaxDistance === 'function') {
            tool.setMaxDistance(camera.distance);
        }
        lastFramedSceneVersionRef.current = sceneVersion;
    }, [mockupLogos, scene, sceneIsLoading, sceneVersion, style, styleRegions]);
    const prevSceneIsLoadingRef = useRef(null);
    useEffect(() => {
        const wasLoading = prevSceneIsLoadingRef.current;
        prevSceneIsLoadingRef.current = sceneIsLoading;
        if (wasLoading === true && sceneIsLoading === false) {
            setWaitingForFirstFrame(true);
            // Defer to next tick so scene composition effects have run
            const id = setTimeout(() => frameScene(), 0);
            return () => clearTimeout(id);
        }
    }, [sceneIsLoading, frameScene]);
    const handleCameraReset = useCallback(() => {
        frameScene({ force: true });
    }, [frameScene]);
    const handleZoomIn = useCallback(() => {
        const viewer = viewerRef.current;
        if (!viewer) {
            return;
        }
        const tool = viewer.tool;
        if (typeof tool.zoomIn === 'function') {
            tool.zoomIn();
        }
    }, []);
    const handleZoomOut = useCallback(() => {
        const viewer = viewerRef.current;
        if (!viewer) {
            return;
        }
        const tool = viewer.tool;
        if (typeof tool.zoomOut === 'function') {
            tool.zoomOut();
        }
    }, []);
    const handleSaveAndExit = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (recipeSource == null || !canStartSaveExit(saveExitStatus) || saveInFlightRef.current) {
            return;
        }
        const apiKey = (_a = searchParams.get('api_key')) !== null && _a !== void 0 ? _a : '';
        if (!apiKey.trim()) {
            setSaveExitStatus('error');
            setSaveExitError('Missing api_key in the page URL.');
            return;
        }
        saveInFlightRef.current = true;
        setSaveExitStatus('saving');
        setSaveExitError('');
        setRecipeUrlCopyStatus('idle');
        try {
            const recipe = buildRecipeBody({
                recipeSource,
                currentLogos: mockupLogos,
                baselineLogos: recipeLogoBaseline !== null && recipeLogoBaseline !== void 0 ? recipeLogoBaseline : [],
                styleRegions,
                pointsOfMeasure,
            });
            const result = yield saveCustomizerRecipe({ apiKey, recipe });
            setSavedRecipeUrl(result.recipeUrl);
            setSavedRecipeExpiresAt(result.expiresAt);
            setSaveExitStatus('saved');
        }
        catch (error) {
            setSaveExitStatus('error');
            setSaveExitError(error instanceof Error ? error.message : 'Failed to save recipe');
        }
        finally {
            saveInFlightRef.current = false;
        }
    }), [
        mockupLogos,
        pointsOfMeasure,
        recipeLogoBaseline,
        recipeSource,
        saveExitStatus,
        searchParams,
        styleRegions,
    ]);
    const handleRecipeUrlCopy = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!savedRecipeUrl) {
            return;
        }
        try {
            yield navigator.clipboard.writeText(savedRecipeUrl);
            setRecipeUrlCopyStatus('copied');
        }
        catch (_a) {
            setRecipeUrlCopyStatus('error');
        }
    }), [savedRecipeUrl]);
    return {
        config,
        dispatch,
        featureFlags,
        handleDownloadImage,
        handleUpdate,
        handleCameraReset,
        handleZoomIn,
        handleZoomOut,
        handleSaveAndExit,
        handleRecipeUrlCopy,
        hoverGraphicId,
        logoRegions,
        mockupLogos,
        recipeSource,
        recipeUrlCopyStatus,
        saveExitError,
        saveExitStatus,
        savedRecipeExpiresAt,
        savedRecipeUrl,
        scene,
        sceneIsLoading,
        selectedGraphicId,
        stylePreviewUrl,
        styleNotFoundMessage,
        viewerRef,
        visible,
        waitingForFirstFrame,
    };
};
